const roundToTwo = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const toRoundedWholeNumber = (value: unknown) => Math.round(toNumber(value));

export const getActiveOrderProducts = (
  products: any[] = [],
  options: { includeNegativeAmounts?: boolean } = {}
) =>
  products.filter((product) => {
    const quantity = toNumber(product?.quantity);
    const amount = toNumber(product?.amount);
    const tax = toNumber(product?.tax);

    return quantity > 0 && (options.includeNegativeAmounts || amount + tax >= 0);
  });

export const getOrderReviewTotals = (
  orderInfo: any,
  options: { includeNegativeAmounts?: boolean } = {}
) => {
  const products = getActiveOrderProducts(orderInfo?.products || [], options);
  const taxableAmount = roundToTwo(
    products.reduce((sum, product) => sum + toNumber(product?.amount), 0)
  );
  const taxAmount = roundToTwo(
    products.reduce((sum, product) => sum + toNumber(product?.tax), 0)
  );
  const productDiscount = roundToTwo(
    products.reduce((sum, product) => sum + toNumber(product?.discountAmount), 0)
  );

  const rawDiscountAmount = productDiscount > 0
    ? productDiscount
    : roundToTwo(toNumber(orderInfo?.discountAmount));

  const courierCharges = roundToTwo(toNumber(orderInfo?.courierCharges));
  const handlingCharges = roundToTwo(toNumber(orderInfo?.handlingCharges));
  const packingCharges = roundToTwo(toNumber(orderInfo?.packingCharges));
  const paymentGatewayCharges = roundToTwo(toNumber(orderInfo?.paymentGatewayCharges));

  const directReward = roundToTwo(toNumber(orderInfo?.rewardPointAmount));

  const rewardPointAmount = directReward;

  const discountAmount = productDiscount;

  const storedTotalValue = roundToTwo(toNumber(orderInfo?.total_Value));

  const computedTotal = roundToTwo(
    taxableAmount +
    taxAmount +
    courierCharges +
    handlingCharges +
    packingCharges +
    paymentGatewayCharges -
    discountAmount
  );

  const totalBeforeReward = storedTotalValue > 0 ? storedTotalValue : computedTotal;

  const payableAmount = roundToTwo(totalBeforeReward - rewardPointAmount);

  return {
    products,
    taxableAmount,
    taxAmount,
    discountAmount,
    courierCharges,
    handlingCharges,
    packingCharges,
    paymentGatewayCharges,
    rewardPointAmount,
    totalBeforeReward,
    payableAmount
  };
};

export const getOrderListDisplayValue = (order: any) => {
  const displayOrderValue = toNumber(order?.displayOrderValue);
  if (displayOrderValue > 0) {
    return displayOrderValue;
  }

  const totalValue = toNumber(order?.total_Value);
  if (totalValue > 0) {
    return totalValue;
  }

  const grossAmount = toNumber(order?.grossAmount);
  if (grossAmount > 0) {
    return grossAmount;
  }

  const totalAmount = toNumber(order?.totalAmount);
  if (totalAmount > 0) {
    return totalAmount;
  }

  const taxableAmount = toNumber(order?.taxableAmount);
  const taxAmount = toNumber(order?.taxAmount);
  const handlingCharges = toNumber(order?.handlingCharges);
  const courierCharges = toNumber(order?.courierCharges);
  const packingCharges = toNumber(order?.packingCharges);
  const paymentGatewayCharges = toNumber(order?.paymentGatewayCharges);
  const rawDiscountAmount = toNumber(order?.discountAmount);

  const directReward = roundToTwo(toNumber(order?.rewardPointAmount));
  const invoiceRewardTotal = roundToTwo(
    (order?.invoices || []).reduce(
      (sum: number, inv: any) => sum + toNumber(inv?.invoiceRewardPointAmount || inv?.rewardPointAmount),
      0
    )
  );
  const rewardPointAmount = directReward > 0 ? directReward : invoiceRewardTotal;

  const orderTotal = toNumber(order?.total || order?.grossAmount || order?.Total || order?.totalAmount);
  let discountAmount = 0;
  if (orderTotal > 0) {
    const subtotal = taxableAmount + taxAmount + handlingCharges + courierCharges + packingCharges + paymentGatewayCharges;
    discountAmount = Math.max(0, roundToTwo(subtotal - rewardPointAmount - orderTotal));
  } else {
    discountAmount = Math.max(0, roundToTwo(rawDiscountAmount - rewardPointAmount));
  }

  return roundToTwo(
    taxableAmount +
    taxAmount +
    handlingCharges +
    courierCharges +
    packingCharges +
    paymentGatewayCharges -
    discountAmount
  );
};

export const getPendingProductCount = (row: any) => {
  const explicitCount = row?.displayPendingProductCount ?? row?.pendingProductCount;
  if (explicitCount !== undefined && explicitCount !== null) {
    return toNumber(explicitCount);
  }

  const productRows = row?.orderDetail || row?.orderDetails || row?.products;
  const invoiceRows = row?.orderInvoice || row?.invoices || [];

  const getProductKey = (item: any) =>
    String(item?.orderDetailID ?? item?.productID ?? item?.productId ?? item?.productName ?? "");

  const invoicedQuantityByProduct = new Map<string, number>();
  if (Array.isArray(invoiceRows)) {
    invoiceRows.forEach((invoice: any) => {
      const invoiceDetails = invoice?.orderInvoiceDetail || invoice?.invoiceDetails || invoice?.products || [];
      if (!Array.isArray(invoiceDetails)) return;

      invoiceDetails.forEach((item: any) => {
        const productKey = getProductKey(item);
        if (!productKey) return;

        invoicedQuantityByProduct.set(
          productKey,
          (invoicedQuantityByProduct.get(productKey) ?? 0) + toNumber(item?.invoicedQuantity ?? item?.quantity)
        );
      });
    });
  }

  if (Array.isArray(productRows) && productRows.length > 0) {
    return productRows.filter((product: any) => {
      const pendingQuantity = toNumber(product?.pendingQuantity);
      if (pendingQuantity > 0) {
        return true;
      }

      const orderQuantity = toNumber(product?.orderQty ?? product?.quantity);
      const invoicedQuantity = toNumber(product?.invoicedQuantity);
      if (orderQuantity > 0 && product?.invoicedQuantity !== undefined) {
        return orderQuantity - invoicedQuantity > 0;
      }

      const productKey = getProductKey(product);
      if (orderQuantity > 0 && productKey && invoicedQuantityByProduct.has(productKey)) {
        return orderQuantity - (invoicedQuantityByProduct.get(productKey) ?? 0) > 0;
      }

      return orderQuantity > 0 && toNumber(row?.pendingQuantity) > 0 && invoicedQuantityByProduct.size === 0;
    }).length;
  }

  const orderQuantity = toNumber(row?.orderQty ?? row?.quantity);
  const invoicedQuantity = toNumber(row?.invoicedQuantity);
  if (orderQuantity > 0 && invoicedQuantity > 0) {
    return orderQuantity - invoicedQuantity > 0 ? 1 : 0;
  }

  return toNumber(row?.pendingQuantity);
};